// Accurate browser geolocation helper.
//
// Default browser settings (enableHighAccuracy: false, no maximumAge) return an
// IP-based lookup which can be 10-100+ miles off — often pointing at the ISP's
// datacenter, not the user. That produced the "logged in and see myself in a
// whole different location" bug. This helper forces high accuracy, disables the
// stale cache, and gives callers a chance to react to permission errors instead
// of silently jumping to the middle of the USA.
//
// Usage:
//   getAccurateLocation({
//     onSuccess: ({ lat, lon, accuracy }) => setCenter([lat, lon]),
//     onError: (err) => toast.error(...),
//   });

import { toast } from "sonner";

export function getAccurateLocation({
  timeout = 10000,
  maximumAge = 0,
  enableHighAccuracy = true,
  onSuccess,
  onError,
  showToastOnError = true,
} = {}) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    const err = { code: 0, message: "Geolocation not supported by this browser" };
    if (showToastOnError) toast.error(err.message);
    onError?.(err);
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) =>
      onSuccess?.({
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
    (err) => {
      if (showToastOnError) {
        if (err.code === 1) toast.error("Location access denied — enable it in browser settings for accurate centering");
        else if (err.code === 2) toast.error("Location unavailable — GPS/Wi-Fi could not determine your position");
        else if (err.code === 3) toast.error("Location request timed out");
        else toast.error(err.message || "Unable to determine your location");
      }
      onError?.(err);
    },
    { enableHighAccuracy, timeout, maximumAge }
  );
}
